import { useCallback, useEffect, useState } from "react";
import {
    Box,
    Typography,
    Stack,
    Card,
    Alert,
    Snackbar,
    CardContent,
    Pagination,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Grid,
    Chip,
    TextField,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { getWorkflowMapping } from "../../api/apiService";

export default function CampaignWorkflow() {
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState([]);
    const [rowCount, setRowCount] = useState(0);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");

    const totalPages = Math.ceil(rowCount / pageSize) || 0;
    const from = rowCount === 0 ? 0 : (page - 1) * pageSize + 1;
    const to = Math.min(page * pageSize, rowCount);

    const fetchCampaignWorkflows = useCallback(async () => {
        try {
            setLoading(true);

            const res = await getWorkflowMapping(page, pageSize, debouncedSearch);
            const mappings = res?.data?.workflowMappings || [];
            const total = res?.data?.total || 0;

            const workflowData = mappings.map((item) => ({
                id: item.id,
                campaignName: item.campaign?.name || "N/A",
                userEmail: item.user?.email || "N/A",
                campaignId: item.campaignId,
                workflowId: item.workflowId || "N/A",
                workflowName: item.workflowName || "N/A",
                status: item.status || "N/A",
                workflowType: item.workflowType || "N/A",
                totalExecutions: item.metadata?.totalExecutions || 0,
                successfulExecutions: item.metadata?.successfulExecutions || 0,
                failedExecutions: item.metadata?.failedExecutions || 0,
                lastTriggeredAt: item.metadata?.lastTriggeredAt || null,
                createdAt: item.createdAt || null,
                updatedAt: item.updatedAt || null,
            }));

            setRows(workflowData);
            setRowCount(total);
        } catch (e) {
            console.error(e);
            setSnackbar({ open: true, message: "Failed to load campaign workflows", severity: "error" });
        } finally {
            setLoading(false);
        }
    }, [page, pageSize, debouncedSearch]);

    useEffect(() => {
        fetchCampaignWorkflows();
    }, [fetchCampaignWorkflows]);

    // Debounce search input
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(search);
            setPage(1); // Reset to page 1 when search changes
        }, 500);
        return () => clearTimeout(timer);
    }, [search]);

    const formatDate = (dateString) => {
        if (!dateString) return "N/A";
        try {
            return new Date(dateString).toLocaleString();
        } catch {
            return "N/A";
        }
    };

    const columns = [
        {
            field: "campaignName",
            headerName: "Campaign Name",
            width: 200,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', minHeight: 50 }}>
                    {params.value}
                </Box>
            ),
        },
        {
            field: "userEmail",
            headerName: "User Email",
            width: 200,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', minHeight: 50 }}>
                    {params.value}
                </Box>
            ),
        },
        {
            field: "workflowName",
            headerName: "Workflow Name",
            width: 250,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', minHeight: 50 }}>
                    {params.value}
                </Box>
            ),
        },
        {
            field: "workflowId",
            headerName: "Workflow ID",
            width: 150,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', minHeight: 50 }}>
                    {params.value}
                </Box>
            ),
        },
        {
            field: "status",
            headerName: "Status",
            width: 90,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', minHeight: 50 }}>
                    <Chip
                        label={params.value}
                        color={params.value === "active" ? "success" : params.value === "inactive" ? "default" : "warning"}
                        size="small"
                    />
                </Box>
            ),
        },
        {
            field: "totalExecutions",
            headerName: "Total Executions",
            width: 150,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', minHeight: 50 }}>
                    {params.value}
                </Box>
            ),
        },
        {
            field: "successfulExecutions",
            headerName: "Successful",
            width: 120,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', minHeight: 50 }}>
                    <Chip label={params.value} color="success" size="small" variant="outlined" />
                </Box>
            ),
        },
        {
            field: "failedExecutions",
            headerName: "Failed",
            width: 100,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', minHeight: 50 }}>
                    <Chip label={params.value} color="error" size="small" variant="outlined" />
                </Box>
            ),
        },
        {
            field: "workflowType",
            headerName: "Workflow Type",
            width: 180,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', minHeight: 50 }}>
                    {params.value}
                </Box>
            ),
        },
        {
            field: "lastTriggeredAt",
            headerName: "Last Triggered",
            width: 180,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', minHeight: 50 }}>
                    {formatDate(params.value)}
                </Box>
            ),
        },
        {
            field: "createdAt",
            headerName: "Created At",
            width: 180,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', minHeight: 50 }}>
                    {formatDate(params.value)}
                </Box>
            ),
        },
        {
            field: "updatedAt",
            headerName: "Updated At",
            width: 180,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', minHeight: 50 }}>
                    {formatDate(params.value)}
                </Box>
            ),
        },
    ];

    return (
        <Box sx={{ mb: 2 }}>
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
                <Alert onClose={() => setSnackbar((p) => ({ ...p, open: false }))} severity={snackbar.severity} sx={{ width: "100%" }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>

            <Grid sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <Typography variant="h5" fontWeight={600}>Campaign Workflow Mappings</Typography>
                <TextField
                    size="small"
                    placeholder="Search workflows..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    sx={{ width: 280, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    slotProps={{ htmlInput: { maxLength: 100 } }}
                />
            </Grid>

            <Box sx={{ height: 510, width: '100%' }}>
                <Card elevation={0} sx={{ borderRadius: 2 }}>
                    <CardContent>
                        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems="center" spacing={2} sx={{ my: 2 }}>
                            <Typography variant="body2" color="text.secondary">
                                {rowCount === 0 ? 'No records' : `Showing ${from}–${to} of ${rowCount} records`}
                            </Typography>
                            <Pagination count={totalPages} page={page} onChange={(e, value) => setPage(value)} color="primary" shape="rounded" size="small" />
                        </Stack>

                        <DataGrid
                            sx={{ height: 600, borderRadius: 2, p: 1 }}
                            rows={rows}
                            columns={columns}
                            loading={loading}
                            getRowId={(row) => row.id}
                            rowCount={rowCount}
                            paginationMode="server"
                            hideFooterPagination
                            getRowHeight={() => "auto"}
                            disableRowSelectionOnClick
                        />

                        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems="center" spacing={2} sx={{ mt: 2 }}>
                            <Typography variant="body2" color="text.secondary">
                                {rowCount === 0 ? 'No records' : `Showing ${from}–${to} of ${rowCount} records`}
                            </Typography>
                            <FormControl size="small" sx={{ minWidth: 120 }}>
                                <InputLabel id="page-size-label">Rows per page</InputLabel>
                                <Select labelId="page-size-label" value={pageSize} label="Rows per page" onChange={(e) => { setPageSize(e.target.value); setPage(1); }}>
                                    {[10, 20, 50].map((size) => (
                                        <MenuItem key={size} value={size}>{size}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <Pagination count={totalPages} page={page} onChange={(e, value) => setPage(value)} color="primary" shape="rounded" size="small" />
                        </Stack>
                    </CardContent>
                </Card>
            </Box>
        </Box>
    );
}
